import {
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { api, type AppNotification, type SafeUser } from "../lib/api";
import { useToast } from "./toast/ToastProvider";
import { useWS } from "../lib/ws";
import {
  NAV,
  NAV_STATE_KEY,
  COLLAPSE_SIDEBAR_BREAKPOINT,
  COMPACT_SEARCH_BREAKPOINT,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_WIDTH,
  TOP_NAV_HEIGHT,
} from "./nav/constants";
import { NavSideBar } from "./nav/NavSideBar";
import { TopNavBar } from "./nav/TopNavBar";
import { SidebarBoundaryToggle } from "./nav/SidebarBoundaryToggle";

interface LayoutProps {
  user: SafeUser;
  children: ReactNode;
  currentPath: string;
  onLocationChange: (to: string) => void;
}

function getInitials(name: string) {
  const initials = name
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  return initials || "U";
}

export function Layout({
  user,
  children,
  currentPath,
  onLocationChange,
}: Readonly<LayoutProps>) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(NAV_STATE_KEY) === "COLLAPSED";
  });
  const [layoutWidth, setLayoutWidth] = useState(() =>
    typeof window === "undefined"
      ? COLLAPSE_SIDEBAR_BREAKPOINT
      : window.innerWidth,
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const avatarRef = useRef<HTMLButtonElement | null>(null);
  const { show: showToast } = useToast();

  const logout = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>("/api/v1/logout"),
    onSettled: () => {
      window.location.href = "/";
    },
  });

  const notifQuery = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get<AppNotification[]>("/api/v1/notifications"),
    refetchInterval: 60_000,
  });

  const unreadCount = (notifQuery.data ?? []).filter(
    (n) => !n.readAt,
  ).length;

  useWS({
    onEvent: (ev) => {
      if (ev.kind !== "crawl.status") return;
      const sourceLabel = ev.sourceName ?? ev.sourceId ?? "unknown";
      if (ev.status === "failed") {
        showToast(
          "Crawl failed",
          `Source ${sourceLabel} failed to sync.`,
          "error",
        );
        notifQuery.refetch();
      } else if (ev.status === "succeeded") {
        showToast(
          "Crawl completed",
          `Source ${sourceLabel} synced successfully.`,
          "success",
        );
        notifQuery.refetch();
      }
    },
  });

  const visibleNav = NAV.filter((n) => !n.adminOnly || user.role === "admin");
  const useHeaderMenu = layoutWidth < COLLAPSE_SIDEBAR_BREAKPOINT;
  const useCompactTitle = layoutWidth < COMPACT_SEARCH_BREAKPOINT;
  let sidebarWidth: number;
  if (useHeaderMenu) {
    sidebarWidth = 0;
  } else if (isCollapsed) {
    sidebarWidth = SIDEBAR_COLLAPSED_WIDTH;
  } else {
    sidebarWidth = SIDEBAR_WIDTH;
  }
  const initials = getInitials(user.username);

  useEffect(() => {
    const onResize = () => setLayoutWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(
      NAV_STATE_KEY,
      isCollapsed ? "COLLAPSED" : "OPENED",
    );
  }, [isCollapsed]);

  useEffect(() => {
    setIsDrawerOpen(false);
    setIsUserMenuOpen(false);
  }, [currentPath]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        userMenuRef.current &&
        avatarRef.current &&
        !userMenuRef.current.contains(target) &&
        !avatarRef.current.contains(target)
      ) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  const navigate = (to: string) => {
    onLocationChange(to);
    setIsDrawerOpen(false);
  };

  const sidebar = (
    <NavSideBar
      currentPath={currentPath}
      isCollapsed={useHeaderMenu ? false : isCollapsed}
      items={visibleNav}
      mode={useHeaderMenu ? "drawer" : "fixed"}
      onLocationChange={navigate}
      onClose={() => setIsDrawerOpen(false)}
      isVisible={isDrawerOpen}
    />
  );

  return (
    <div className="min-h-screen bg-datahub-gray-1600 font-sans text-datahub-gray-600">
      <TopNavBar
        initials={initials}
        isUserMenuOpen={isUserMenuOpen}
        notificationCount={unreadCount}
        onLocationChange={navigate}
        onLogout={() => logout.mutate()}
        onSidebarToggle={() => setIsDrawerOpen((current) => !current)}
        onUserMenuToggle={() => setIsUserMenuOpen((current) => !current)}
        refAvatar={avatarRef}
        refMenu={userMenuRef}
        showBrandIcon={useHeaderMenu}
        showCompactTitle={useCompactTitle}
        showSidebarToggle={useHeaderMenu}
        sidebarWidth={sidebarWidth}
        username={user.username}
      />

      {!useHeaderMenu && sidebar}
      {useHeaderMenu && sidebar}

      {!useHeaderMenu && (
        <SidebarBoundaryToggle
          isCollapsed={isCollapsed}
          left={sidebarWidth}
          onClick={() => setIsCollapsed((current) => !current)}
        />
      )}

      <main
        className="fixed bottom-0 right-0 overflow-y-auto bg-datahub-gray-1600 p-4"
        style={{
          top: TOP_NAV_HEIGHT,
          left: sidebarWidth,
          transition: useHeaderMenu ? "none" : "left 250ms ease-in-out",
        }}
      >
        <div className="min-w-0">{children}</div>
      </main>
    </div>
  );
}
